'use client';

import { X, GripVertical, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface WidgetCardProps {
  id: string;
  title: string;
  icon?: LucideIcon;
  isNew?: boolean;
  onHide?: (id: string) => void;
  actions?: ReactNode;
  className?: string;
  /** Controalele de personalizare (grip drag + X ascunde) apar DOAR în modul
   *  „Aranjează" — permanent vizibile erau zgomot pe un dashboard de citit. */
  showControls?: boolean;
  children: ReactNode;
}

export function WidgetCard({ id, title, icon: Icon, isNew, onHide, actions, className = '', showControls = true, children }: WidgetCardProps) {
  return (
    <div
      data-widget-id={id}
      style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
      className={
        'group relative bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-5 pt-4 transition-all hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-[0_2px_10px_rgba(15,14,15,0.04)] overflow-hidden flex flex-col ' +
        className
      }
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        {/* 0.6563rem era greu lizibil, mai ales pe mobil (audit P3) — 0.75rem
            cu tracking puțin mai strâns păstrează limbajul mono-uppercase. */}
        <span className="inline-flex items-center gap-1.5 min-w-0 text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          {/* Mâner de reordonare — drag DOAR de aici. Restul cardului rămâne scrollabil pe
              touch (fix scroll mobil: SortableJS handle scoped la [data-drag-grip]). */}
          {showControls && (
            <span
              data-drag-grip
              role="button"
              aria-label="Trage pentru a reordona"
              title="Trage pentru a reordona"
              className="shrink-0 -ml-1.5 -my-1 p-1.5 rounded-md text-neutral-300 dark:text-neutral-600 hover:text-neutral-500 dark:hover:text-neutral-300 cursor-grab active:cursor-grabbing touch-none select-none"
            >
              <GripVertical className="h-4 w-4" />
            </span>
          )}
          {Icon && <Icon className="h-3 w-3 opacity-80" />}
          <span className="truncate">{title}</span>
          {isNew && (
            <span className="text-[0.5625rem] font-bold tracking-[0.08em] uppercase bg-primary text-white px-1.5 py-0.5 rounded-[3px]">
              nou
            </span>
          )}
        </span>
        <div className="flex items-center gap-1">
          {actions}
          {onHide && showControls && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onHide(id);
              }}
              title="Ascunde widget"
              className="p-1 rounded-md text-neutral-400 dark:text-neutral-500 opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400 transition-all"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="relative z-[1] flex-1 min-h-0 flex flex-col justify-center">{children}</div>
    </div>
  );
}
